// Requirements Service - API functions for managing requirements
import apiClient from '../../../../../shared/lib/apiClient';
import { UserFlow, HighLevelRequirement, LowLevelRequirement, TestCase } from '../../../../../shared/types/project';

export interface CreateUserFlowPayload {
  uiid: string;
  name: string;
  description?: string;
  project_id: string;
}

export interface CreateHighLevelRequirementPayload {
  uiid: string;
  name: string;
  description?: string;
  parent_uiid: string;
  project_id: string;
}

export interface CreateLowLevelRequirementPayload {
  uiid: string;
  name: string;
  description?: string;
  parent_uiid: string;
  project_id: string;
}

export interface CreateTestCasePayload {
  uiid: string;
  name: string;
  description?: string;
  parent_uiid: string;
  project_id: string;
}

export interface UpdateRequirementPayload {
  name?: string;
  description?: string;
}

class RequirementsService {
  // User Flow operations
  async createUserFlow(projectId: string, payload: CreateUserFlowPayload): Promise<UserFlow> {
    return apiClient<UserFlow>(`/requirements/${projectId}/flows`, {
      method: 'POST',
      body: payload,
    });
  }

  async deleteUserFlow(projectId: string, uiid: string): Promise<void> {
    return apiClient<void>(`/requirements/${projectId}/flows/${uiid}`, {
      method: 'DELETE',
    });
  }

  async updateUserFlow(projectId: string, uiid: string, payload: UpdateRequirementPayload): Promise<UserFlow> {
    return apiClient<UserFlow>(`/requirements/${projectId}/flows/${uiid}`, {
      method: 'PATCH',
      body: { ...payload, uiid },
    });
  }

  // High Level Requirement operations
  async createHighLevelRequirement(
    projectId: string,
    payload: CreateHighLevelRequirementPayload
  ): Promise<HighLevelRequirement> {
    return apiClient<HighLevelRequirement>(`/requirements/${projectId}/high-level-requirements`, {
      method: 'POST',
      body: payload,
    });
  }

  async deleteHighLevelRequirement(projectId: string, uiid: string): Promise<void> {
    return apiClient<void>(`/requirements/${projectId}/high-level-requirements/${uiid}`, {
      method: 'DELETE',
    });
  }

  async updateHighLevelRequirement(
    projectId: string,
    uiid: string,
    payload: UpdateRequirementPayload
  ): Promise<HighLevelRequirement> {
    return apiClient<HighLevelRequirement>(`/requirements/${projectId}/high-level-requirements/${uiid}`, {
      method: 'PATCH',
      body: { ...payload, uiid },
    });
  }

  // Low Level Requirement operations
  async createLowLevelRequirement(
    projectId: string,
    payload: CreateLowLevelRequirementPayload
  ): Promise<LowLevelRequirement> {
    return apiClient<LowLevelRequirement>(`/requirements/${projectId}/low-level-requirements`, {
      method: 'POST',
      body: payload,
    });
  }

  async deleteLowLevelRequirement(projectId: string, uiid: string): Promise<void> {
    return apiClient<void>(`/requirements/${projectId}/low-level-requirements/${uiid}`, {
      method: 'DELETE',
    });
  }

  async updateLowLevelRequirement(
    projectId: string,
    uiid: string,
    payload: UpdateRequirementPayload
  ): Promise<LowLevelRequirement> {
    return apiClient<LowLevelRequirement>(`/requirements/${projectId}/low-level-requirements/${uiid}`, {
      method: 'PATCH',
      body: { ...payload, uiid },
    });
  }

  async createTestCase(projectId: string, payload: CreateTestCasePayload): Promise<TestCase> {
    return apiClient<TestCase>(`/requirements/${projectId}/test-cases`, {
      method: 'POST',
      body: payload,
    });
  }

  async updateTestCase(projectId: string, uiid: string, payload: UpdateRequirementPayload): Promise<TestCase> {
    return apiClient<TestCase>(`/requirements/${projectId}/test-cases/${uiid}`, {
      method: 'PATCH',
      body: { ...payload, uiid },
    });
  }
}

export default new RequirementsService();













